/**
 * The meta line under a document's filename in the Folders and List views:
 * size · when it was uploaded · who uploaded it.
 *
 * A signed container is a file like every other file in the list, so its row
 * renders this exact line — same fields, same order. Everything
 * container-specific (enclosed documents, signatures, the ASiC-E label) lives in
 * the signature panel the row opens, not inline.
 */

import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

import { formatDocumentDateTime } from "../../utils/documentDates";
import { DateHint } from "./DateHint";

interface DocumentRowMetaProps {
  /** File size in bytes. */
  size?: number | null;
  /** Upload timestamp (`created_at`), rendered in full: "03-09-2026 14:05". */
  createdAt: string;
  createdByName?: string | null;
  /** Mirrors the list's Uploader column visibility. */
  showUser?: boolean;
  /**
   * Never wrap: squeezed, the uploader's name truncates and the rest clips, so
   * the row keeps its height (the Brief page's `singleLineRows`).
   */
  singleLine?: boolean;
  className?: string;
}

export function DocumentRowMeta({
  size,
  createdAt,
  createdByName,
  showUser = true,
  singleLine = false,
  className,
}: DocumentRowMetaProps) {
  const { t } = useLanguage();
  const userShown = Boolean(showUser && createdByName);
  // On one line the size and first separator stay whole. The uploader gives
  // way first (it shrinks far faster than the date), down to a floor that
  // still fits an ellipsis; only then does the date, also with an ellipsis,
  // so a squeezed line never ends in a digit cut in half.
  const fixed = singleLine ? "flex-shrink-0" : undefined;
  const uploadedAt = formatDocumentDateTime(createdAt);

  return (
    <div
      className={cn(
        "mt-1 flex items-center gap-2 text-xs text-muted-foreground",
        singleLine ? "flex-nowrap overflow-hidden whitespace-nowrap" : "flex-wrap",
        className
      )}
    >
      <span className={fixed}>{((size ?? 0) / 1024).toFixed(1)} KB</span>
      <span className={fixed}>•</span>
      <DateHint
        // On one line the date can be ellipsised, so its tooltip carries it too.
        hint={
          singleLine
            ? `${uploadedAt} · ${t.documents.table.uploadedTooltip}`
            : t.documents.table.uploadedTooltip
        }
        className={cn("whitespace-nowrap", singleLine && "min-w-0 truncate")}
      >
        {uploadedAt}
      </DateHint>
      {userShown &&
        (singleLine ? (
          // The separator truncates with the name as one run of text, so the
          // line can end in "•…" but never in a bare "•".
          <span className="min-w-8 shrink-[1000] truncate">
            <span aria-hidden="true" className="mr-1.5">
              •
            </span>{" "}
            {createdByName}
          </span>
        ) : (
          <>
            <span>•</span>
            <span className="truncate">{createdByName}</span>
          </>
        ))}
    </div>
  );
}
